<template>
    <div class="receivable-channels">
        <div class="page-header">
            <h2>收款渠道管理</h2>
            <el-tooltip
                :disabled="availableTypes.length > 0"
                content="所有渠道类型均已配置"
                placement="top"
            >
                <el-button type="primary" :disabled="availableTypes.length === 0" @click="showAddDialog = true">
                    <el-icon><Plus /></el-icon>
                    新增渠道
                </el-button>
            </el-tooltip>
        </div>

        <div class="channel-list" v-if="channelList.length > 0">
            <div class="channel-item" v-for="channel in channelList" :key="channel.type">
                <div class="channel-header">
                    <div class="channel-title">
                        <el-icon class="channel-icon" :color="channel.color">
                            <component :is="channel.icon" />
                        </el-icon>
                        <span>{{ channel.label }}</span>
                    </div>
                    <div class="channel-actions">
                        <template v-if="channel.supportDefault">
                            <el-tag v-if="channel.data.defaultFlag == 1" type="success" effect="plain">默认渠道</el-tag>
                            <el-button v-else-if="channel.data.id" type="warning" text @click="handleSetDefault(channel)">
                                设为默认渠道
                            </el-button>
                        </template>
                        <el-button type="danger" text @click="handleDelete(channel)">
                            <el-icon><Delete /></el-icon>
                            删除
                        </el-button>
                    </div>
                </div>

                <el-form
                    :ref="el => setFormRef(el, channel.type)"
                    :model="channel.data"
                    :rules="getRules(channel)"
                    label-width="0"
                    class="channel-form"
                >
                    <div class="channel-content">
                        <div class="field-group" v-for="(field, index) in getFields(channel)" :key="index">
                            <div class="field-label">{{ field.label }}</div>
                            <div class="field-value">
                                <el-form-item :prop="field.key">
                                    <el-select
                                        v-if="channel.editing && field.type === 'select'"
                                        v-model="channel.data[field.key]"
                                        :placeholder="field.placeholder"
                                        clearable
                                    >
                                        <el-option
                                            v-for="option in getSelectOptions(field)"
                                            :key="option.value"
                                            :label="option.label"
                                            :value="option.value"
                                        />
                                    </el-select>
                                    <el-input
                                        v-else-if="channel.editing"
                                        v-model="channel.data[field.key]"
                                        :placeholder="field.placeholder"
                                        :type="field.type === 'password' ? 'password' : 'text'"
                                    />
                                    <el-tooltip
                                        v-else-if="field.key === 'publicKeyStr' || field.key === 'privateKeyStr'"
                                        :content="channel.data[field.key] || '-'"
                                        placement="top"
                                        :show-after="500"
                                    >
                                        <div class="key-field">
                                            <span class="key-text">{{ channel.data[field.key] || '-' }}</span>
                                            <el-button
                                                v-if="channel.data[field.key]"
                                                type="primary"
                                                text
                                                size="small"
                                                @click="handleCopy(channel.data[field.key])"
                                            >
                                                <el-icon><CopyDocument /></el-icon>
                                            </el-button>
                                        </div>
                                    </el-tooltip>
                                    <span v-else-if="field.type === 'select'">{{ getSelectLabel(field, channel.data[field.key]) }}</span>
                                    <span v-else>{{ channel.data[field.key] || '-' }}</span>
                                </el-form-item>
                            </div>
                        </div>
                    </div>
                </el-form>

                <div class="channel-footer">
                    <template v-if="!channel.editing">
                        <el-button type="primary" size="small" @click="handleEdit(channel)">
                            <el-icon><Edit /></el-icon>
                            编辑
                        </el-button>
                    </template>
                    <template v-else>
                        <el-button size="small" @click="handleCancel(channel)">取消</el-button>
                        <el-button type="primary" size="small" @click="handleSave(channel)">保存</el-button>
                    </template>
                </div>
            </div>
        </div>

        <el-empty v-else description="暂无收款渠道配置，请点击右上角新增" />

        <!-- 新增渠道类型选择弹窗 -->
        <el-dialog v-model="showAddDialog" title="选择渠道类型" width="400px" :close-on-click-modal="false">
            <div class="type-select-list">
                <div
                    class="type-select-item"
                    v-for="type in availableTypes"
                    :key="type.value"
                    @click="handleAddChannel(type.value)"
                >
                    <el-icon class="type-icon" :color="type.color">
                        <component :is="type.icon" />
                    </el-icon>
                    <span>{{ type.label }}</span>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete, CreditCard, Wallet, CopyDocument } from '@element-plus/icons-vue';
import { required, isUrl, isAlphanumeric } from '@/utils/validators';

export default {
    name: 'ReceivableChannels',
    components: {
        Plus,
        Edit,
        Delete,
        CreditCard,
        Wallet,
        CopyDocument
    },
    data() {
        return {
            channelList: [],
            showAddDialog: false,
            formRefs: {},
            channelTypes: [
                { value: 'poly', label: '招行支付配置', requestKey: 'polyPayConfig', icon: 'CreditCard', color: '#409EFF', supportDefault: true },
                { value: 'polyScore', label: '招行微信支付分配置', requestKey: 'polyPayScoreConfig', icon: 'CreditCard', color: '#409EFF', supportDefault: true },
                { value: 'wxScore', label: '微信支付分配置', requestKey: 'wxPayScoreConfig', icon: 'Wallet', color: '#07C160', supportDefault: true }
            ],
            payTypeOptions: [
                { value: 1, label: '平台账户' },
                { value: 2, label: '支付宝APP' },
                { value: 3, label: '微信APP' },
                { value: 4, label: '银联' },
                { value: 5, label: '一卡通' },
                { value: 6, label: '会员卡' },
                { value: 7, label: '服务费优惠币' },
                { value: 8, label: '线下支付' },
                { value: 9, label: '支付宝小程序支付' },
                { value: 10, label: '微信小程序支付' },
                { value: 11, label: '数字人民币' },
                { value: 12, label: '建行联合支付' },
                { value: 13, label: '微信支付分' },
                { value: 14, label: '招商银行支付' },
                { value: 15, label: '招行微信支付分' }
            ],
            channelTypeOptions: [
                { value: 1, label: '充值退款' },
                { value: 2, label: '支付分' },
                { value: 3, label: '混合' }
            ],
            polyFields: [
                { key: 'payType', label: '支付渠道', placeholder: '请选择支付渠道', type: 'select', optionsKey: 'payType' },
                { key: 'appId', label: 'appId', placeholder: 'appId' },
                { key: 'merId', label: '商户号', placeholder: '商户号' },
                { key: 'useId', label: '账号', placeholder: '账号' },
                { key: 'domain', label: '域名', placeholder: 'https://cloud.echargnet.com/pay-server' },
                { key: 'secret', label: '密钥', placeholder: '密钥', type: 'password' },
                { key: 'polyUrl', label: '聚合支付接口', placeholder: 'https://api.cmbchina.com/polypay/v1.0' },
                { key: 'subappId', label: '聚合支付子商户appId', placeholder: '聚合支付子商户appId' },
                { key: 'subMerId', label: '聚合支付子商户merId', placeholder: '聚合支付子商户merId' },
                { key: 'publicKeyStr', label: '公钥', placeholder: '公钥' },
                { key: 'privateKeyStr', label: '私钥', placeholder: '私钥', type: 'password' }
            ],
            polyScoreFields: [
                { key: 'payType', label: '支付渠道', placeholder: '请选择支付渠道', type: 'select', optionsKey: 'payType' },
                { key: 'channelType', label: '渠道类型', placeholder: '请选择渠道类型', type: 'select', optionsKey: 'channelType' },
                { key: 'appId', label: 'appId', placeholder: 'appId' },
                { key: 'merId', label: '商户号', placeholder: '商户号' },
                { key: 'userId', label: '账号', placeholder: '账号' },
                { key: 'domain', label: '域名', placeholder: 'https://cloud.echargnet.com/pay-server' },
                { key: 'secret', label: '密钥', placeholder: '密钥', type: 'password' },
                { key: 'polyUrl', label: '聚合支付接口', placeholder: 'https://api.cmbchina.com/polypay/v1.0' },
                { key: 'subAppId', label: '聚合支付子商户appId', placeholder: '聚合支付子商户appId' },
                { key: 'serviceId', label: '支付分服务id', placeholder: '支付分服务id' },
                { key: 'publicKeyStr', label: '公钥', placeholder: '银行公钥（验证招行支付分回调SM2签名）' },
                { key: 'privateKeyStr', label: '私钥', placeholder: '私钥', type: 'password' }
            ],
            wxScoreFields: [
                { key: 'payType', label: '支付渠道', placeholder: '请选择支付渠道', type: 'select', optionsKey: 'payType' },
                { key: 'channelType', label: '渠道类型', placeholder: '请选择渠道类型', type: 'select', optionsKey: 'channelType' },
                { key: 'appId', label: '应用ID', placeholder: '应用ID（公众号/小程序appId）' },
                { key: 'mchId', label: '商户号', placeholder: '商户号' },
                { key: 'serviceId', label: '支付分服务ID', placeholder: '支付分服务ID（微信下发，支付分核心配置）' },
                { key: 'domain', label: '域名', placeholder: 'https://cloud.echargnet.com/pay-server' },
                { key: 'serialNumber', label: '商户API证书序列号', placeholder: '商户API证书序列号' },
                { key: 'publicKeyId', label: '微信支付平台证书序列号', placeholder: '微信支付平台证书序列号' },
                { key: 'keyPath', label: '商户API私钥路径', placeholder: '商户API私钥路径' },
                { key: 'platformCertPath', label: '微信支付平台证书路径', placeholder: '微信支付平台证书路径' },
                { key: 'publicKeyPath', label: '微信支付平台证书公钥路径', placeholder: '微信支付平台证书公钥路径（回调验签）' },
                { key: 'apiKey3', label: 'APIv3密钥', placeholder: 'APIv3密钥（回调报文解密）', type: 'password' },
                { key: 'riskFundName', label: '风险资金类型', placeholder: '默认ESTIMATE_ORDER_COST 预估订单费用' },
                { key: 'riskFundLimit', label: '押金上限（分）', placeholder: '押金上限（单位：分）' }
            ]
        };
    },
    computed: {
        availableTypes() {
            const existingTypes = this.channelList.map(c => c.type);
            return this.channelTypes.filter(t => !existingTypes.includes(t.value));
        }
    },
    mounted() {
        this.loadAllChannels();
    },
    methods: {
        loadAllChannels() {
            const that = this;
            that.$axios.post(that.$store.state.platformApi + 'PayConfig/list',{}).then(result => {
                if (result.code == 200 && result.data) {
                    that.channelList = [];
                    if (result.data.polyPayConfig) {
                        that.channelList.push(that.buildChannelData('poly', result.data.polyPayConfig));
                    }
                    if (result.data.polyPayScoreConfig) {
                        that.channelList.push(that.buildChannelData('polyScore', result.data.polyPayScoreConfig));
                    }
                    if (result.data.wxPayScoreConfig) {
                        that.channelList.push(that.buildChannelData('wxScore', result.data.wxPayScoreConfig));
                    }
                }
            }).catch(() => {});
        },
        buildChannelData(type, configData) {
            const typeConfig = this.channelTypes.find(t => t.value === type);
            const formData = { id: configData.id, defaultFlag: configData.defaultFlag };
            this.getFields({ type }).forEach(field => {
                formData[field.key] = configData[field.key];
            });
            return {
                type: type,
                label: typeConfig.label,
                requestKey: typeConfig.requestKey,
                icon: typeConfig.icon,
                color: typeConfig.color,
                supportDefault: !!typeConfig.supportDefault,
                editing: false,
                originalData: JSON.parse(JSON.stringify(formData)),
                data: formData
            };
        },
        getFields(channel) {
            const fieldsMap = {
                poly: this.polyFields,
                polyScore: this.polyScoreFields,
                wxScore: this.wxScoreFields
            };
            return fieldsMap[channel.type] || this.polyFields;
        },
        getSelectOptions(field) {
            const optionsMap = {
                payType: this.payTypeOptions,
                channelType: this.channelTypeOptions
            };
            return optionsMap[field.optionsKey] || [];
        },
        getSelectLabel(field, value) {
            if (value === undefined || value === null || value === '') return '-';
            const option = this.getSelectOptions(field).find(o => o.value === value);
            return option ? option.label : value;
        },
        getRules(channel) {
            if (!channel.editing) {
                return {};
            }
            const rulesMap = {
                poly: {
                    payType: [required('请输入支付渠道')],
                    appId: [required('请输入appId')],
                    merId: [required('请输入商户号'), isAlphanumeric('商户号只能输入数字或字母')],
                    useId: [required('请输入账号')],
                    domain: [required('请输入域名'), isUrl('请输入正确的域名格式')],
                    secret: [required('请输入密钥')],
                    polyUrl: [required('请输入聚合支付接口'), isUrl('请输入正确的接口地址格式')],
                    subappId: [isAlphanumeric('子商户appId只能输入数字或字母')],
                    subMerId: [isAlphanumeric('子商户merId只能输入数字或字母')],
                    publicKeyStr: [required('请输入公钥')],
                    privateKeyStr: [required('请输入私钥')]
                },
                polyScore: {
                    payType: [required('请选择支付渠道')],
                    channelType: [required('请选择渠道类型')],
                    appId: [required('请输入appId')],
                    merId: [required('请输入商户号'), isAlphanumeric('商户号只能输入数字或字母')],
                    userId: [required('请输入账号')],
                    domain: [required('请输入域名'), isUrl('请输入正确的域名格式')],
                    secret: [required('请输入密钥')],
                    polyUrl: [required('请输入聚合支付接口'), isUrl('请输入正确的接口地址格式')],
                    subAppId: [isAlphanumeric('子商户appId只能输入数字或字母')],
                    serviceId: [required('请输入支付分服务id')],
                    publicKeyStr: [required('请输入公钥')],
                    privateKeyStr: [required('请输入私钥')]
                },
                wxScore: {
                    payType: [required('请选择支付渠道')],
                    channelType: [required('请选择渠道类型')],
                    appId: [required('请输入应用ID')],
                    mchId: [required('请输入商户号'), isAlphanumeric('商户号只能输入数字或字母')],
                    serviceId: [required('请输入支付分服务ID')],
                    domain: [required('请输入域名'), isUrl('请输入正确的域名格式')],
                    serialNumber: [required('请输入商户API证书序列号')],
                    publicKeyId: [required('请输入微信支付平台证书序列号')],
                    keyPath: [required('请输入商户API私钥路径')],
                    platformCertPath: [required('请输入微信支付平台证书路径')],
                    publicKeyPath: [required('请输入微信支付平台证书公钥路径')],
                    apiKey3: [required('请输入APIv3密钥')],
                    riskFundName: [required('请输入风险资金类型')],
                    riskFundLimit: [required('请输入押金上限')]
                }
            };
            return rulesMap[channel.type] || {};
        },
        setFormRef(el, type) {
            if (el) {
                this.formRefs[type] = el;
            }
        },
        handleAddChannel(type) {
            this.showAddDialog = false;
            const typeConfig = this.channelTypes.find(t => t.value === type);
            const emptyData = {};
            this.getFields({ type }).forEach(field => {
                emptyData[field.key] = '';
            });
            const newChannel = {
                type: type,
                label: typeConfig.label,
                requestKey: typeConfig.requestKey,
                icon: typeConfig.icon,
                color: typeConfig.color,
                supportDefault: !!typeConfig.supportDefault,
                editing: true,
                originalData: JSON.parse(JSON.stringify(emptyData)),
                data: emptyData
            };
            this.channelList.push(newChannel);
        },
        handleEdit(channel) {
            channel.originalData = JSON.parse(JSON.stringify(channel.data));
            channel.editing = true;
        },
        handleCancel(channel) {
            channel.data = JSON.parse(JSON.stringify(channel.originalData));
            channel.editing = false;
            const formRef = this.formRefs[channel.type];
            if (formRef) {
                formRef.clearValidate();
            }
        },
        handleSave(channel) {
            const that = this;
            const formRef = this.formRefs[channel.type];
            if (!formRef) return;
            
            formRef.validate((valid) => {
                if (!valid) return;
                
                const isNewChannel = !channel.data.id;
                const url = isNewChannel ? 'PayConfig/add' : 'PayConfig/edit';
                
                const params = { [channel.requestKey]: channel.data };
                
                that.$axios.post(that.$store.state.platformApi + url, JSON.stringify(params)).then(result => {
                    if (result.code == 200) {
                        ElMessage.success(isNewChannel ? '新增成功' : '编辑成功');
                        channel.editing = false;
                        channel.originalData = JSON.parse(JSON.stringify(channel.data));
                        formRef.clearValidate();
                        that.loadAllChannels();
                    }
                });
            });
        },
        handleDelete(channel) {
            const that = this;
            const typeName = channel.label;
            ElMessageBox.confirm(`确定要删除${typeName}吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                if (channel.data.id) {
                    that.$axios.post(that.$store.state.platformApi + 'PayConfig/del/' + channel.data.id, {}).then(result => {
                        if (result.code == 200) {
                            ElMessage.success('删除成功');
                            that.channelList = that.channelList.filter(c => c.type !== channel.type);
                        }
                    });
                } else {
                    that.channelList = that.channelList.filter(c => c.type !== channel.type);
                    ElMessage.success('删除成功');
                }
            }).catch(() => {});
        },
        handleSetDefault(channel) {
            const that = this;
            that.$axios.post(that.$store.state.platformApi + 'PayConfig/setDefault/' + channel.data.id, {}).then(result => {
                if (result.code == 200) {
                    ElMessage.success('设置成功');
                    // 同channelType下只保留一个默认渠道，重新加载以同步所有渠道状态
                    that.loadAllChannels();
                }
            });
        },
        handleCopy(text) {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(() => {
                    ElMessage.success('复制成功');
                }).catch(() => {
                    ElMessage.error('复制失败');
                });
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                try {
                    document.execCommand('copy');
                    ElMessage.success('复制成功');
                } catch (err) {
                    ElMessage.error('复制失败');
                }
                document.body.removeChild(textarea);
            }
        }
    }
};
</script>

<style scoped>
.receivable-channels {
    padding: 20px;
    background-color: #f5f7fa;
    min-height: 100vh;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 16px 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.page-header h2 {
    margin: 0;
    font-size: 18px;
    color: #303133;
}

.channel-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.channel-item {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    transition: box-shadow 0.3s;
}

.channel-item:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.channel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #ebeef5;
    background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
}

.channel-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.channel-icon {
    font-size: 20px;
}

.channel-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.channel-form {
    width: 100%;
}

.channel-form .el-form-item {
    margin-bottom: 0;
    width: 100%;
}

.channel-form .el-form-item__error {
    position: static;
    padding-top: 4px;
}

:deep(.channel-form .el-input) {
    width: 100%;
}

:deep(.channel-form .el-input__wrapper) {
    min-height: 40px !important;
    height: 40px !important;
    padding: 0 12px !important;
}

:deep(.channel-form .el-input__inner) {
    height: 40px !important;
    line-height: 40px !important;
    font-size: 14px;
}

:deep(.channel-form .el-select) {
    width: 100%;
}

:deep(.channel-form .el-select .el-input__wrapper) {
    min-height: 40px !important;
    height: 40px !important;
}

:deep(.channel-form .el-select .el-input__inner) {
    height: 40px !important;
    line-height: 40px !important;
    font-size: 14px;
}

.channel-content {
    padding: 20px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
}

.field-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-label {
    font-size: 14px;
    color: #606266;
    font-weight: 500;
}

.field-value {
    font-size: 14px;
    color: #303133;
    padding: 0;
    background: transparent;
    border-radius: 6px;
    min-height: 40px;
    display: flex;
    align-items: center;
}

.field-value .el-form-item {
    width: 100%;
    margin-bottom: 0;
}

.key-field {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.key-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
}

.channel-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 20px;
    border-top: 1px solid #ebeef5;
    background: #fafbfc;
}

.type-select-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.type-select-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border: 2px solid #ebeef5;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 15px;
}

.type-select-item:hover {
    border-color: #409EFF;
    background: #ecf5ff;
}

.type-icon {
    font-size: 24px;
}

:deep(.el-empty) {
    padding: 60px 0;
}
</style>